import { useState, useRef, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import SideBar from '../SideBar.js';
import Header from '../../Header/Header.js';
import { FaAngleDown } from 'react-icons/fa';
import { CREATE_SUPPORT } from '../../graphql/Mutation/Support.js';
import { useMutation } from '@apollo/client';
import { toast } from "react-toastify";
import SpinLoader from "../../components/SpinLoader.js";
import { useLocation } from "react-router-dom";
import { useFileUpload } from "../../Common/PreSignUrl.jsx";
import { IMAGE_URL } from "../../config.js";

const ShipmentRelated = () => {
    const { handleFileUpload } = useFileUpload();
    const location = useLocation();
    const selectedIssue = location.state?.selectedIssue || ""; // Get issue title from state
    const [isCategoryOpen, setCategoryOpen] = useState(false);
    const [isTypeOpen, setTypeOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [shipmentAttachment, setShipmentAttachment] = useState("");


    const [charCount, setCharCount] = useState(0);
    const maxChars = 300;
    const issueCategories = ["Request", "Complaint", "Query"];
    const issueTypeMapping = {
        Request: [
            "OTP Request",
            "Urgent Delivery",
            "Reattempt",
            "RTO Instruction",
            "Self collect - Branch address required",
            "Alternate contact / address",
            "POD required",
            "Tech Error",
            "Hold shipment"
        ],
        Complaint: [
            "Fake NDR",
            "Forward stuck in transit",
            "RTO stuck in transit",
            "Fake delivery updated",
            "RTO undelivered",
            "Wrong product received"
        ],
        Query: ["Shipment Status", "Claim Status"]
    };
    const [handleinputsupport, { loading }] = useMutation(CREATE_SUPPORT);
    const formik = useFormik({
        initialValues: {
            awbNumbers: "",
            issueCategory: "",
            issueType: "",
            remarks: "",
            attachment: null,
        },
        validationSchema: Yup.object({
            awbNumbers: Yup.lazy((_, context) => {
                return context.parent.issueCategory !== "Pickups Related Issues"
                    ? Yup.string().required("AWB Numbers are required")
                    : Yup.string().nullable();
            }),
            issueCategory: Yup.lazy((_, context) => {
                return selectedIssue === "Shipments Related Issues"
                    ? Yup.string().required("Please select an issue category")
                    : Yup.string().nullable();
            }),
            issueType: Yup.lazy((_, context) => {
                return selectedIssue === "Shipments Related Issues"
                    ? Yup.string().required("Please select an issue type")
                    : Yup.string().nullable();
            }),
            // issueCategory: Yup.string().required("Please select an issue category"),
            // issueType: Yup.string().required("Please select an issue type"),
            remarks: Yup.string()
                .nullable()
                .max(500, "Maximum 500 characters allowed"),
            attachment: Yup.mixed()
                .nullable()
                .test("fileSize", "File size should be less than 5MB", (value) => {
                    if (!value) return true;
                    return value.size <= 5 * 1024 * 1024;
                }),
        }),
        onSubmit: async (values, { resetForm }) => {
            try {
                const response = await handleinputsupport({
                    variables: {
                        input: {
                            ticket_type_issue: selectedIssue === "Shipments Related Issues" ? "Shipment" : "Pickups",
                            awbNo: values.awbNumbers,
                            issue_category: values.issueCategory,
                            issue_type: values.issueType,
                            message: {
                                sender: "seller",
                                message: values?.remarks,
                                attachments: shipmentAttachment ? [shipmentAttachment] : [],
                            },
                        },
                    },
                });

                // console.log('✌️response --->', response);
                if (response?.data?.createSupport?.status) {
                    toast.success(response.data.createSupport.message);
                    setShipmentAttachment("");
                    resetForm(); // Reset the form after successful submission
                } else {
                    toast.error(response.data.createSupport.message);
                }
            } catch (err) {
                toast.error(err);
            }
        },
    });
    const categoryRef = useRef(null);
    const typeRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (categoryRef.current && !categoryRef.current.contains(event.target)) {
                setCategoryOpen(false);
            }
            if (typeRef.current && !typeRef.current.contains(event.target)) {
                setTypeOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);
    const handleFileChange = (event, keyPrefix, folderName) => {
        if ((!event?.target?.files || !event?.currentTarget?.files) || event?.target?.files?.length === 0) {
            setIsLoading(false);
            // console.log("===");
        }
        handleFileUpload({
            event,
            keyPrefix,
            setStateCallback: (url) => {
                const newUrl = url.split('/').slice(-2).join('/');
                // console.log('✌️newUrl --->', newUrl);
                setShipmentAttachment(newUrl)
                setIsLoading(false);
            },
            setIsLoading,
            folderName
        });
    }
    return (
        <>
            <Header />

            <div className="flex min-h-screen bg-slate-100 overflow-hidden">
                <SideBar />
                <div className="flex-1 space-y-3 md:p-6 py-4 overflow-x-hidden ml-16">
                    <div className="max-w-3xl justify-start bg-white p-6 rounded-lg shadow-lg">
                        <h2 className="text-xl font-bold text-gray-800">{selectedIssue}</h2>
                        <p className="text-sm text-gray-500 mb-10 mt-2">Fill the form below to raise a request.</p>

                        <form onSubmit={formik.handleSubmit}>
                            {/* AWB Numbers Textarea */}
                            <div className="mb-6 md:p-6 md:bg-slate-100">
                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                    AWB Numbers (Comma Separated)
                                </label>
                                <textarea
                                    name="awbNumbers"
                                    className="w-full mt-1 p-3 border border-gray-300 rounded-lg  focus:outline-none"
                                    placeholder="Enter AWB numbers separated by commas"
                                    {...formik.getFieldProps("awbNumbers")}
                                />
                                {formik.touched.awbNumbers && formik.errors.awbNumbers ? (
                                    <p className="text-red-500 text-sm">{formik.errors.awbNumbers}</p>
                                ) : null}
                            </div>

                            {/* Issue Category Dropdown */}
                            {selectedIssue !== "Pickups Related Issues" && <div className="relative mb-6" ref={categoryRef}>
                                <label className="block text-sm font-medium mb-1 text-gray-700">Select Issue Category</label>
                                <div
                                    className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-lg  flex justify-between items-center cursor-pointer"
                                    onClick={() => {
                                        setCategoryOpen(!isCategoryOpen);
                                        setTypeOpen(false); // Close other dropdown
                                    }}

                                >
                                    {formik.values.issueCategory || "Choose one"}
                                    <FaAngleDown className="text-gray-400" />
                                </div>

                                {isCategoryOpen && (
                                    <ul className="absolute left-0 w-full bg-white border border-gray-300 mt-1 rounded-lg shadow-lg z-10">
                                        {issueCategories?.map((category) => (
                                            <li
                                                key={category}
                                                className="px-3 py-1.5 hover:bg-slate-100 cursor-pointer"
                                                onClick={() => {
                                                    formik.setFieldValue("issueCategory", category);
                                                    formik.setFieldValue("issueType", "");
                                                    setCategoryOpen(false);
                                                }}
                                            >
                                                {category}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {formik.touched.issueCategory && formik.errors.issueCategory ? (
                                    <p className="text-red-500 text-sm">{formik.errors.issueCategory}</p>
                                ) : null}
                            </div>}

                            {/* Issue Type Dropdown */}
                            {selectedIssue !== "Pickups Related Issues" && <div className="relative mb-6">
                                <label className="block text-sm mb-1 font-medium text-gray-700">Select Issue Type</label>
                                <div
                                    className="w-full mt-1 px-3 py-1.5 border border-gray-300 rounded-lg  flex justify-between items-center cursor-pointer"
                                    onClick={() => {
                                        setTypeOpen(!isTypeOpen);
                                        setCategoryOpen(false); // Close other dropdown
                                    }}
                                >
                                    {formik.values.issueType || "Choose one"}
                                    <FaAngleDown className="text-gray-400" />
                                </div>

                                {isTypeOpen && (
                                    <ul className="absolute left-0 w-full bg-white border border-gray-300 mt-1 rounded-lg shadow-lg z-10">
                                        {isTypeOpen && (
                                            <ul className="absolute left-0 w-full bg-white border border-gray-300 mt-1 rounded-lg shadow-lg z-10">
                                                {issueTypeMapping[formik.values.issueCategory]?.map((type) => (
                                                    <li
                                                        key={type}
                                                        className="px-3 py-2 hover:bg-slate-100 cursor-pointer"
                                                        onClick={() => {
                                                            formik.setFieldValue("issueType", type);
                                                            setTypeOpen(false);
                                                        }}
                                                    >
                                                        {type}
                                                    </li>
                                                ))}
                                            </ul>
                                        )}

                                        {/* {issueTypes?.map((type) => (
                                            <li
                                                key={type}
                                                className="px-3 py-1.5 hover:bg-slate-100 cursor-pointer"
                                                onClick={() => {
                                                    formik.setFieldValue("issueType", type);
                                                    setTypeOpen(false);
                                                }}
                                            >
                                                {type}
                                            </li>
                                        ))} */}
                                    </ul>
                                )}
                                {formik.touched.issueType && formik.errors.issueType ? (
                                    <p className="text-red-500 text-sm">{formik.errors.issueType}</p>
                                ) : null}
                            </div>}

                            {/* Remarks Textarea with Character Counter */}
                            <div className="mb-6">
                                <label className="block text-sm mb-1 font-medium text-gray-700">Remarks (Optional)</label>
                                <textarea
                                    name="remarks"
                                    className="w-full mt-1 p-3 border border-gray-300 rounded-lg focus:outline-none"
                                    placeholder="Enter Remark"
                                    maxLength={maxChars}
                                    onChange={(e) => {
                                        setCharCount(e.target.value.length);
                                        formik.setFieldValue("remarks", e.target.value);
                                    }}
                                    value={formik.values.remarks}
                                />
                                <p className={`text-sm ${charCount === maxChars ? "text-red-500 text-sm" : "text-gray-500"}`}>
                                    {charCount} / {maxChars} Characters Remaining
                                </p>
                            </div>

                            {/* File Upload */}
                            <div className="mb-6">
                                <label className="block text-sm mb-2 font-medium text-gray-700">
                                    Attachments (If any)
                                </label>
                                <div className="relative border-2 border-dashed border-gray-300 p-6 rounded-lg w-full text-center cursor-pointer hover:border-slate-800 transition">
                                    <input
                                        type="file"
                                        accept="image/*,application/pdf"
                                        className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                                        // onChange={(event) => {
                                        //     formik.setFieldValue("attachment", event.currentTarget.files[0]);
                                        // }}
                                        onChange={(e) => handleFileChange(e, "shipmentIssue", "ticket")}
                                    />
                                    <div className="flex flex-col items-center">
                                        <svg
                                            className="w-10 h-10 text-gray-400 mb-2"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            viewBox="0 0 24 24"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M12 16v-4m0 0v-4m0 4h4m-4 0H8m-4 8a9 9 0 1118 0H4z"
                                            ></path>
                                        </svg>
                                        <p className="text-gray-700 font-medium">Click to upload or drag & drop</p>
                                        <p className="text-xs text-gray-500">Maximum file size: 5MB</p>
                                    </div>
                                </div>
                                {shipmentAttachment && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-700">Image Preview:</p>
                                        <img
                                            src={`${IMAGE_URL}/${shipmentAttachment}`}
                                            alt="Preview"
                                            className="mt-2 w-full h-auto rounded-lg shadow-md"
                                        />
                                    </div>
                                )}
                                {/* {formik.touched.attachment && formik.errors.attachment && (
                                    <p className="text-red-500 text-sm mt-2">{formik.errors.attachment}</p>
                                )} */}
                            </div>


                            {/* Submit Button */}
                            <button
                                type="submit"
                                className="w-full py-2 bg-slate-800 text-white font-medium rounded-lg hover:bg-slate-900 transition flex items-center justify-center gap-2"
                            >
                                Submit {loading && <SpinLoader />}
                            </button>

                        </form>
                    </div>
                </div>
            </div>
        </>
    )
}

export default ShipmentRelated
